/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

/**
 * Provides navigation links for various sections of the site
 *
 * @module enervee_utils.navigation
 */
NavLinksService.$inject = ['$q', '$http', '$timeout', 'UtilService'];
function NavLinksService($q, $http, $timeout, UtilService) {
    var _pub = {};
    var _customNavLinksPromise;
    var _customMobileNavLinks = {
        navLinks: null,
    };
    var _faqLinkClicked = false;
    var _clickedLinks = [];
    var _customHeaderDropdownLinks = null;
    var _customPlatformLinks = null;
    var _customOtherSitesLinks = null;
    var _customSupportLinks = null;
    var _customLegalLinks = null;
    var _footerLinks = null;

    /**
     * Goes through all custom nav links and builds an object of mobile nav links and a list
     * of mini header links depending on the links' attributes.
     * @param {Array} navLinks
     * @returns {*}
     */
    var prepareNavLinks = function(navLinks) {
        _customMobileNavLinks = {flat: [], nested: []};
        _customHeaderDropdownLinks = [];
        _customPlatformLinks = [];
        _customOtherSitesLinks = [];
        _customSupportLinks = [];
        _customLegalLinks = [];
        _footerLinks = [];
        navLinks.forEach(function(navLink){
            // first let's build the mobile nav link object
            if (navLink.types.indexOf('mobile_nav') !== -1) {
                if (navLink.children.length === 0){
                    _customMobileNavLinks.flat.push(navLink);
                } else {
                    _customMobileNavLinks.nested.push(navLink);
                }
            } else {
                navLink.children.forEach(function(childLink){
                    if (childLink.types.indexOf('mobile_nav') !== -1){
                        _customMobileNavLinks.flat.push(childLink);
                    }
                });
            }
            // next let's build the footer link list
            if (navLink.types.indexOf('footer') !== -1) {
                _footerLinks.push(navLink);
            }

            // next let's build the header dropdown link list
            if (navLink.types.indexOf('header_dropdown') !== -1) {
                _customHeaderDropdownLinks.push(navLink);
            } else {
                navLink.children.forEach(function(childLink){
                    if (childLink.types.indexOf('header_dropdown') !== -1){
                        _customHeaderDropdownLinks.push(childLink);
                    }
                });
            }

            // next let's build the platform list
            if (navLink.types.indexOf('platform') !== -1) {
                _customPlatformLinks.push(navLink);
            }

            // next let's build the "other sites" link list
            if (navLink.types.indexOf('other_sites') !== -1) {
                _customOtherSitesLinks.push(navLink);
            }

            // next let's build the support link list
            if (navLink.types.indexOf('support') !== -1) {
                _customSupportLinks.push(navLink);
            }

            // next let's build the legal link list
            if (navLink.types.indexOf('legal') !== -1) {
                _customLegalLinks.push(navLink);
            }

        });
    };

    /**
     * Fetches all custom navigation links from the CMS in list format.
     * @returns {*}
     */
    _pub.fetchCustomNavLinks = function() {
        if (!_customNavLinksPromise) {
            var deferred = $q.defer();
            $http.get(
                UtilService.getSiteSetting('cms_base_url') + 'navigation/navigation-links/'
            ).then(function(response){
                prepareNavLinks(response.data);
                deferred.resolve();
            }, function(error){
                _customMobileNavLinks = {
                    navLinks: null,
                };
                deferred.reject();
            });
            _customNavLinksPromise = deferred.promise;
        }
        return _customNavLinksPromise;
    };

    /**
     * Helper function that either returns the mobile nav links or null
     * @returns {*}
     */
    var buildMobileNavLinks = function() {
        if (_customMobileNavLinks !== null) {
            return {
                navLinks: _customMobileNavLinks
            };
        } else {
            return null;
        }
    };

    /**
     * Returns the custom mobile nav links.
     * @returns {*}
     */
    _pub.getCustomMobileNavLinks = function() {
        var deferred = $q.defer();
        if (_customMobileNavLinks !== null && _customMobileNavLinks.navLinks !== null) {
            deferred.resolve(buildMobileNavLinks());
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(buildMobileNavLinks());
            }, function() {
                deferred.resolve(buildMobileNavLinks());  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    /**
     * Returns the custom header dropdown links.
     * @returns {*}
     */
    _pub.getCustomHeaderDropdownLinks = function () {
        var deferred = $q.defer();
        if (_customHeaderDropdownLinks) {
            deferred.resolve(_customHeaderDropdownLinks);
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(_customHeaderDropdownLinks);
            }, function() {
                deferred.resolve(_customHeaderDropdownLinks);  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    /**
     * Returns the custom header dropdown links.
     * @returns {*}
     */
    _pub.getCustomPlatformLinks = function () {
        var deferred = $q.defer();
        if (_customPlatformLinks) {
            deferred.resolve(_customPlatformLinks);
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(_customPlatformLinks);
            }, function() {
                deferred.resolve(_customPlatformLinks);  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    _pub.getShowDropdownLinks = function () {
        var headerLinksPromise = _pub.getCustomHeaderDropdownLinks();
        var platformLinksPromise = _pub.getCustomPlatformLinks();
        return $q.all([headerLinksPromise, platformLinksPromise]).then(function () {
            if (_customPlatformLinks.length === 0 && _customHeaderDropdownLinks.length === 0) {
                return false;
            }
            return {
                'platformLinks': _customPlatformLinks,
                'headerDropdownLinks': _customHeaderDropdownLinks
            };
        });
    };

    /**
     * Returns "other sites" links.
     * @returns {$q.promise}
     */
    _pub.getOtherSitesLinks = function () {
        var deferred = $q.defer();
        if (_customOtherSitesLinks) {
            deferred.resolve(_customOtherSitesLinks);
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(_customOtherSitesLinks);
            }, function() {
                deferred.resolve(_customOtherSitesLinks);  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    /**
     * Returns the support links.
     * @returns {$q.promise}
     */
    _pub.getSupportLinks = function () {
        var deferred = $q.defer();
        if (_customSupportLinks) {
            deferred.resolve(_customSupportLinks);
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(_customSupportLinks);
            }, function() {
                deferred.resolve(_customSupportLinks);  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    /**
     * Returns the legal links.
     * @returns {$q.promise}
     */
    _pub.getLegalLinks = function () {
        var deferred = $q.defer();
        if (_customLegalLinks) {
            deferred.resolve(_customLegalLinks);
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(_customLegalLinks);
            }, function() {
                deferred.resolve(_customLegalLinks);  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    /**
     * Returns the footer links from CMS.
     * @returns {$q.promise}
     */
    _pub.getFooterLinks = function() {
        var deferred = $q.defer();
        if (_footerLinks) {
            deferred.resolve(_footerLinks);
        } else {
            _pub.fetchCustomNavLinks().then(function() {
                deferred.resolve(_footerLinks);
            }, function() {
                deferred.resolve(_footerLinks);  // is set to empty array on request failure
            });
        }
        return deferred.promise;
    };

    /**
     * Helper function to return the FAQ page link (if one is set).
     * If no site setting for it exists, the default path to the
     * Enervee support page is returned.
     *
     * @return {String}
     */
    _pub.getErrorPageFAQLink = function() {
        var marketplaceDomain = UtilService.getSetting('marketplaceDomain');
        return UtilService.getSiteSetting('error_page_faq_link', marketplaceDomain + 'support/');
    };

    /**
     * Helper function for tracking links.
     * Sends the necessary tracking information for when the user
     * clicks on a link. Also handles stopping the initial event and
     * re-clicking once the tracking has finished (which in most cases
     * is near immediate).
     * @param {String} href The link's href property
     * @param {Object} trackingParams Any properties for the link's
     * tracking event that need to be overridden.
     *
     * @example
     * '<a ng-click="trackLinkAndReClick($event, trackingParams)" href="...">Link</a>'
     */
    _pub.trackLinkAndReClick = function(e, trackingParams) {
        var hrefIndex = _clickedLinks.indexOf(e.target.href);
        var linkHasNotBeenClicked = hrefIndex === -1;
        if (linkHasNotBeenClicked) {
            e.preventDefault();
            e.stopPropagation();
            var target = e.target;
            $timeout(function() {
                _clickedLinks.push(target.href);
                target.click();
            });
        } else {
            _clickedLinks.slice(hrefIndex, 1);
        }
    };

    /**
     * Helper function to handle sending tracking events for anchor
     * tags to the support page, and waiting until the tracking event
     * is completed before navigating away (and probably resetting the
     * javascript).
     *
     * @param {Object} e The $event from an `ng-click` attribute
     * @param {Object} trackingParams Any properites for the link's
     * tracking event that need to be overridden.
     */
    _pub.handleFAQLinkClick = function(e, trackingParams) {
        var defaultFAQParams = {
            eventAction: 'Clicked LinkFAQ',
            attributes: {
                pageSection: 'Footer',
            }
        };
        trackingParams = Object.assign({}, defaultFAQParams, trackingParams);
        _pub.trackLinkAndReClick(e, trackingParams);
    };

    return _pub;
}

module.exports = NavLinksService;
